import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { getBlackCard, updateBlackCard } from "Api/apiService";

const emptyBenefit = {
  title: "",
  description: "",
  icon: "",
};

export default function BlackCardAdmin() {
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [benefits, setBenefits] = useState([emptyBenefit]);
  const [stepsText, setStepsText] = useState("");
  const [ctaTitle, setCtaTitle] = useState("");
  const [ctaBody, setCtaBody] = useState("");
  const [ctaFootnote, setCtaFootnote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const textColor = useColorModeValue("navy.700", "white");
  const labelColor = useColorModeValue("secondaryGray.600", "whiteAlpha.700");

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await getBlackCard();
      const payload = response?.data?.blackCard;
      setHeroTitle(payload?.heroTitle || "");
      setHeroSubtitle(payload?.heroSubtitle || "");
      if (Array.isArray(payload?.benefits) && payload.benefits.length > 0) {
        setBenefits(payload.benefits);
      } else {
        setBenefits([emptyBenefit]);
      }
      setStepsText(
        Array.isArray(payload?.steps) ? payload.steps.join("\n") : ""
      );
      setCtaTitle(payload?.ctaTitle || "");
      setCtaBody(payload?.ctaBody || "");
      setCtaFootnote(payload?.ctaFootnote || "");
    } catch (error) {
      console.error("Failed to load black card content", error);
      toast({
        title: "Unable to load Black Card content.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const handleBenefitChange = (index, key) => (event) => {
    const value = event.target.value;
    setBenefits((prev) =>
      prev.map((benefit, idx) =>
        idx === index ? { ...benefit, [key]: value } : benefit
      )
    );
  };

  const addBenefit = () => {
    setBenefits((prev) => [...prev, emptyBenefit]);
  };

  const removeBenefit = (index) => {
    setBenefits((prev) =>
      prev.length === 1 ? prev : prev.filter((_, idx) => idx !== index)
    );
  };

  const handleSave = async () => {
    if (!heroTitle.trim() || !heroSubtitle.trim()) {
      toast({
        title: "Hero title and subtitle are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!ctaTitle.trim() || !ctaBody.trim() || !ctaFootnote.trim()) {
      toast({
        title: "CTA title, body, and footnote are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        heroTitle: heroTitle.trim(),
        heroSubtitle: heroSubtitle.trim(),
        benefits: benefits
          .filter((benefit) => benefit.title.trim() && benefit.description.trim())
          .map((benefit) => ({
            title: benefit.title.trim(),
            description: benefit.description.trim(),
            icon: benefit.icon.trim(),
          })),
        steps: stepsText
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        ctaTitle: ctaTitle.trim(),
        ctaBody: ctaBody.trim(),
        ctaFootnote: ctaFootnote.trim(),
      };
      await updateBlackCard(payload);
      toast({
        title: "Black Card updated.",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      loadContent();
    } catch (error) {
      console.error("Failed to update Black Card", error);
      toast({
        title: "Unable to update Black Card.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid columns={{ base: 1 }} spacing="20px">
        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Black Card Information
            </Text>
            <Text fontSize="14px" color={labelColor}>
              Update hero copy, benefits, and CTA text.
            </Text>
            <Divider />
            <Stack spacing="14px">
              <FormControl>
                <FormLabel color={labelColor}>Hero Title</FormLabel>
                <Input value={heroTitle} onChange={(e) => setHeroTitle(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor}>Hero Subtitle</FormLabel>
                <Input
                  value={heroSubtitle}
                  onChange={(e) => setHeroSubtitle(e.target.value)}
                />
              </FormControl>
            </Stack>
          </Flex>
        </Card>

        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Flex justify="space-between" align="center">
              <Text fontSize="22px" fontWeight="700" color={textColor}>
                Benefits
              </Text>
              <Button variant="outline" onClick={addBenefit}>
                Add Benefit
              </Button>
            </Flex>
            <Divider />
            <Stack spacing="14px">
              {benefits.map((benefit, index) => (
                <Box
                  key={`benefit-${index}`}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="12px"
                  p="16px"
                >
                  <Flex justify="space-between" align="center" mb="12px">
                    <Text fontWeight="700" color={textColor}>
                      Benefit {index + 1}
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeBenefit(index)}
                      isDisabled={benefits.length === 1}
                    >
                      Remove
                    </Button>
                  </Flex>
                  <Stack spacing="12px">
                    <FormControl>
                      <FormLabel color={labelColor}>Icon (emoji)</FormLabel>
                      <Input
                        value={benefit.icon}
                        onChange={handleBenefitChange(index, "icon")}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Title</FormLabel>
                      <Input
                        value={benefit.title}
                        onChange={handleBenefitChange(index, "title")}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel color={labelColor}>Description</FormLabel>
                      <Textarea
                        value={benefit.description}
                        onChange={handleBenefitChange(index, "description")}
                        rows={3}
                      />
                    </FormControl>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Flex>
        </Card>

        <Card p="24px">
          <Flex direction="column" gap="16px">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Steps & CTA
            </Text>
            <Divider />
            <Stack spacing="14px">
              <FormControl>
                <FormLabel color={labelColor}>Steps (one per line)</FormLabel>
                <Textarea
                  value={stepsText}
                  onChange={(e) => setStepsText(e.target.value)}
                  rows={4}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor}>CTA Title</FormLabel>
                <Input value={ctaTitle} onChange={(e) => setCtaTitle(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor}>CTA Body</FormLabel>
                <Textarea
                  value={ctaBody}
                  onChange={(e) => setCtaBody(e.target.value)}
                  rows={3}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor}>CTA Footnote</FormLabel>
                <Input
                  value={ctaFootnote}
                  onChange={(e) => setCtaFootnote(e.target.value)}
                />
              </FormControl>
            </Stack>
          </Flex>
        </Card>

        <Flex justify="flex-end">
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={loading}
          >
            Save Changes
          </Button>
        </Flex>
      </SimpleGrid>
    </Box>
  );
}
